import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Bot, MessageSquare, Smartphone, Tablet, Users, ShieldCheck, Check, ArrowRight,
  Clock, Sparkles, CheckCircle2, ChevronDown, Zap, Calendar, HeartPulse, Building2,
  HardHat, ShoppingBag, Lock, HelpCircle, Star, Send, ExternalLink
} from 'lucide-react';

const managerAssistantFeatures = [
  {
    icon: Calendar,
    title: 'Smart Shift Rota Drafting',
    desc: 'Ask the assistant to populate your weekly schedule based on employee availability, contracted hours, and shift templates.'
  },
  {
    icon: Zap,
    title: 'Live Labor Cost Monitoring',
    desc: 'The assistant tracks your real-time hourly burn rate against your daily sales targets so labor spend stays in budget.'
  },
  {
    icon: Clock,
    title: 'Sickness & Overlap Alerts',
    desc: 'When an employee reports absence, the copilot immediately flags shift gaps and suggests qualified, available replacements.'
  },
  {
    icon: ShieldCheck,
    title: 'UK Compliance & Day-1 SSP Calculations',
    desc: 'Built specifically for UK employment rules, providing instant statutory sick pay figures and 12.07% holiday accrual breakdowns.'
  }
];

const whatsappAssistantFeatures = [
  {
    icon: Smartphone,
    title: 'Zero App Downloads for Staff',
    desc: 'Frontline workers do not need to download another company app, remember complex passwords, or update phone software.'
  },
  {
    icon: Clock,
    title: 'Instant Shift & Rota Lookups',
    desc: 'Staff message "When do I work next?" and get their upcoming start times, locations, and coworker details in one second.'
  },
  {
    icon: Calendar,
    title: '12.07% Holiday Booking & Balances',
    desc: 'Employees ask "What is my holiday balance?" to see accrued days and submit leave requests directly in their chat.'
  },
  {
    icon: CheckCircle2,
    title: '1-Tap Manager WhatsApp Approvals',
    desc: 'Managers receive an instant ping on WhatsApp with interactive [Approve] and [Decline] buttons for leave and shift swaps.'
  }
];

const faqs = [
  {
    q: 'Is the WhatsApp Assistant live right now?',
    a: 'The Dashboard Manager Copilot is live and ready today inside your KithHR web portal (free for 1 to 15 staff). The Frontline WhatsApp Assistant is currently undergoing final Meta WhatsApp Business API integration and closed testing. You can set up your kiosk and rotas today — when public rollout begins, all workspaces with 16+ employees (PRO) will receive WhatsApp access automatically with zero added fees.'
  },
  {
    q: 'Why does KithHR provide two different AI assistants?',
    a: 'Workforce management involves two distinct groups: managers and frontline employees. Managers need a powerful copilot inside their desktop dashboard to draft rotas, audit payroll, and track labor costs. Frontline employees (cleaners, retail staff, chefs, carers) rarely sit at computers and dislike downloading workplace apps. Giving staff a WhatsApp assistant in their pocket and managers a dashboard copilot solves communication for both sides.'
  },
  {
    q: 'How does the WhatsApp Assistant pricing work?',
    a: 'For businesses with 1 to 15 employees, KithHR core features (including our tablet PIN kiosk, drag-and-drop rotas, timesheets, and holiday accrual) are 100% Free Forever. The WhatsApp AI Assistant unlocks automatically with zero added fees for all KithHR PRO workspaces (when you reach 16+ staff at £3.49/user/month). The monthly PRO subscription covers all Meta messaging and server charges so you never get surprise add-on bills.'
  },
  {
    q: 'Do employees need to download anything to use the WhatsApp Assistant?',
    a: 'No. Employees simply save your official KithHR WhatsApp business number in their contacts. They send a message (such as "Hi" or "My shifts") and the AI immediately verifies their phone number against your staff roster and answers their questions.'
  },
  {
    q: 'What can employees do through WhatsApp?',
    a: 'Staff can ask for their upcoming schedule, check how many holiday hours they have accrued under the UK 12.07% statutory rule, submit holiday requests, and notify managers of sick days under UK Day-1 SSP rules. The assistant provides immediate, polite, 24/7 responses.'
  },
  {
    q: 'Is company data private and UK GDPR compliant?',
    a: 'Yes. KithHR communicates strictly via official Meta WhatsApp Business Cloud API endpoints hosted in UK/EU Google Cloud data centers (europe-west2, London). We never sell employee data, and messages are encrypted end-to-end between the worker and the assistant.'
  },
  {
    q: 'Can managers approve holiday requests directly on WhatsApp?',
    a: 'Yes. When an employee requests leave via WhatsApp, the manager receives a message card with the employee name, dates, and remaining holiday balance, accompanied by two interactive buttons: [Approve] and [Decline]. Tapping either button instantly updates KithHR and sends a confirmation back to the employee.'
  }
];

const AiWorkplaceAssistants: React.FC = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'both' | 'manager' | 'whatsapp'>('both');

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'KithHR Dual-AI Workplace Assistants',
        operatingSystem: 'Web, iOS, Android, WhatsApp',
        applicationCategory: 'BusinessApplication',
        url: 'https://kithhr.co.uk/ai-workplace-assistants/',
        offers: [
          {
            '@type': 'Offer',
            name: 'KithHR Core (1-15 Staff)',
            price: '0.00',
            priceCurrency: 'GBP',
            description: 'Free workforce scheduling and PIN kiosk with Dashboard AI Copilot included.'
          },
          {
            '@type': 'Offer',
            name: 'KithHR PRO (16+ Staff)',
            price: '3.49',
            priceCurrency: 'GBP',
            description: 'Includes 24/7 Frontline WhatsApp AI Assistant, GPS Geofencing, and Live Radar Map.'
          }
        ],
        description: 'KithHR pairs a Manager AI Copilot inside the dashboard with a Frontline WhatsApp AI Assistant in staff pockets. Free for 1-15 staff; WhatsApp unlocks at 16+ seats.'
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a
          }
        }))
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://kithhr.co.uk/'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'AI Workplace Assistants',
            item: 'https://kithhr.co.uk/ai-workplace-assistants/'
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0F172A] font-sans selection:bg-[#0062D2] selection:text-white">
      <SEO
        title="KithHR Dual-AI: Manager Copilot & Frontline WhatsApp Assistant"
        description="Two AI assistants working together: a Manager Copilot inside the dashboard and a 24/7 WhatsApp Assistant for frontline staff. Free for 1-15 staff; WhatsApp unlocks at 16+ seats."
        canonical="https://kithhr.co.uk/ai-workplace-assistants/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
      />

      {/* HEADER / NAVIGATION */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E2D8] transition-all">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 font-extrabold text-2xl tracking-tight text-[#0F172A] hover:opacity-90">
            <img src="/logo.png" alt="KithHR Logo" className="w-9 h-9 object-contain" />
            <span>Kith<span className="text-[#0062D2]">HR</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/tablet-time-clock-kiosk"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              Tablet Kiosk
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              Pricing
            </Link>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95 cursor-pointer"
            >
              Start Free (1-15 Staff)
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 overflow-hidden border-b border-[#E8E2D8]">
        <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-bold tracking-normal mb-6 shadow-xs">
            <Users size={15} className="text-emerald-700" />
            <span>Built for Real UK Workforces &amp; Frontline Shifts</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] max-w-4xl mx-auto leading-[1.12]">
            Two AI assistants. <br className="hidden sm:block" />
            <span className="text-[#0062D2]">One for managers</span> in the dashboard. <br className="hidden sm:block" />
            <span className="text-emerald-600">One in WhatsApp</span> for your staff.
          </h1>

          <p className="mt-6 text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Frontline teams do not want another workplace app to download. Managers do not have time for late-night schedule texts. KithHR pairs a desktop dashboard copilot (live today) with an upcoming 24/7 WhatsApp assistant so neither side has to chase shifts.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-4 rounded-2xl text-base transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              Get Started Free <ArrowRight size={18} />
            </button>
            <a
              href="#dual-comparison"
              className="w-full sm:w-auto bg-white border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-6 py-4 rounded-2xl text-base transition-all shadow-xs"
            >
              See How Both Work ↓
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs text-[#64748B] font-medium">
            <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Dashboard Copilot live today</span>
            <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Free forever for 1-15 staff</span>
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-amber-600 stroke-[2.5]" /> WhatsApp in final Meta testing (16+ staff)</span>
          </div>
        </div>
      </section>

      {/* SIDE-BY-SIDE DUAL AI COMPARISON */}
      <section id="dual-comparison" className="py-20 md:py-28 bg-[#FDFBF7] border-b border-[#E8E2D8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A] tracking-tight">
              Built for the reality of shift-based work
            </h2>
            <p className="mt-4 text-[#475569] text-base leading-relaxed">
              Every day, managers spend hours on rotas while staff flood group chats asking when they work. Here is how KithHR solves both workflows simultaneously.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            
            {/* CARD 1: MANAGER AI COPILOT */}
            <div className="bg-white rounded-3xl border-2 border-blue-200/80 p-8 shadow-sm flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-[11px] font-black uppercase tracking-wider px-4 py-1.5 rounded-bl-2xl">
                Live &amp; Ready Today
              </div>

              <div>
                <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-[#0062D2] mb-6">
                  <Bot size={26} />
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Assistant #1</span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs font-semibold text-gray-500">For Employers &amp; Shift Supervisors</span>
                </div>

                <h3 className="text-2xl font-black text-[#0F172A] mb-3">
                  Dashboard Manager Copilot
                </h3>

                <p className="text-sm text-[#475569] leading-relaxed mb-6">
                  An intelligent operations assistant embedded inside your KithHR web dashboard. It acts as an experienced shift planner and HR advisor by your side.
                </p>

                {/* Simulated Dashboard Chat Window */}
                <div className="bg-[#0F172A] rounded-2xl p-4 text-white font-mono text-xs mb-6 shadow-inner space-y-3">
                  <div className="flex items-start gap-2.5">
                    <span className="text-blue-400 font-bold shrink-0">Manager:</span>
                    <span className="text-gray-200">"Draft a 4-person Saturday shift for Central Cafe under £520 labor budget."</span>
                  </div>
                  <div className="border-t border-slate-700/80 pt-2.5 flex items-start gap-2.5">
                    <span className="text-emerald-400 font-bold shrink-0">Copilot:</span>
                    <span className="text-gray-300">
                      "Drafted 4 shifts (2 Baristas, 1 Floor, 1 Cook) totaling 34 hours. Est. cost: £424.32 (22.5% of projected sales). No compliance overlaps."
                    </span>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {managerAssistantFeatures.map((feat, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={14} className="stroke-[3]" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-[#0F172A]">{feat.title}</h4>
                        <p className="text-xs text-[#64748B] mt-0.5">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-[#E8E2D8] flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500 block">Availability</span>
                  <span className="text-sm font-bold text-emerald-700">100% Free Forever (1-15 Staff)</span>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-[#0062D2] hover:bg-[#004CB5] text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all cursor-pointer"
                >
                  Try in Dashboard
                </button>
              </div>
            </div>

            {/* CARD 2: FRONTLINE WHATSAPP ASSISTANT */}
            <div className="bg-white rounded-3xl border-2 border-emerald-200/80 p-8 shadow-sm flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-amber-500 text-white text-[11px] font-black uppercase tracking-wider px-4 py-1.5 rounded-bl-2xl flex items-center gap-1.5 shadow-xs">
                <Clock size={12} className="stroke-[3]" /> In Development • Coming Soon
              </div>

              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 mb-6">
                  <MessageSquare size={26} />
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Assistant #2</span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs font-semibold text-gray-500">For Hourly &amp; Frontline Workers</span>
                </div>

                <h3 className="text-2xl font-black text-[#0F172A] mb-3">
                  Frontline WhatsApp Assistant
                </h3>

                <p className="text-sm text-[#475569] leading-relaxed mb-4">
                  A conversational AI assistant that will live directly inside your employees' existing WhatsApp. No app stores, no passwords, and zero staff onboarding friction.
                </p>

                {/* Explicit Status Box to prevent user confusion */}
                <div className="mb-5 p-3.5 rounded-2xl bg-amber-50/90 border border-amber-200 text-amber-950 text-xs flex items-start gap-2.5 shadow-xs">
                  <Clock size={16} className="text-amber-700 shrink-0 mt-0.5" />
                  <div className="leading-snug">
                    <span className="font-bold text-amber-900 block mb-0.5">Rollout Status: In Closed Beta</span>
                    Currently completing final Meta WhatsApp Business API verification. Unlocks automatically with zero extra fees for all 16+ staff PRO workspaces upon public release.
                  </div>
                </div>

                {/* Simulated WhatsApp Chat Window */}
                <div className="bg-[#ECE5DD] rounded-2xl p-4 text-xs mb-6 shadow-inner space-y-2.5 border border-emerald-100">
                  <div className="flex justify-end">
                    <div className="bg-[#E7FFDB] text-[#111B21] px-3.5 py-2 rounded-xl rounded-tr-xs shadow-xs max-w-[80%]">
                      When is my next shift?
                      <div className="text-[10px] text-gray-400 text-right mt-0.5">14:22</div>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white text-[#111B21] px-3.5 py-2 rounded-xl rounded-tl-xs shadow-xs max-w-[85%] space-y-1">
                      <span>👋 Hi Elena! Your next shift is:</span>
                      <div className="font-bold text-[#0F172A]">📅 Saturday 14:00 – 22:00</div>
                      <div className="text-[11px] text-gray-600">📍 Central Store (Front Counter)</div>
                      <div className="text-[10px] text-gray-400 text-right mt-1">14:22</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {whatsappAssistantFeatures.map((feat, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={14} className="stroke-[3]" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-[#0F172A]">{feat.title}</h4>
                        <p className="text-xs text-[#64748B] mt-0.5">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-[#E8E2D8] flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500 block">Pricing Rule</span>
                  <span className="text-sm font-bold text-[#0F172A]">Included Free at 16+ Staff</span>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-100/90 px-3 py-1.5 rounded-full border border-amber-300">
                    <Clock size={12} className="text-amber-700" /> Coming Soon (Private Beta)
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* HOW THE PRICING & UNLOCK MODEL WORKS */}
      <section className="py-20 bg-white border-b border-[#E8E2D8]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-3 py-1 rounded-full mb-3">
              Clear &amp; Fair Economics
            </div>
            <h2 className="text-3xl font-black text-[#0F172A] tracking-tight">
              Why does the WhatsApp Assistant unlock at 16 employees?
            </h2>
            <p className="mt-3 text-[#475569] text-base leading-relaxed">
              We believe in honest software pricing with no surprise add-on fees. Here is our simple policy for small teams and growing workforces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#FDFBF7] border border-[#E8E2D8] rounded-3xl p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-emerald-700 bg-emerald-100/60 px-3 py-1 rounded-full">
                  1 to 15 Employees
                </span>
                <span className="text-2xl font-black text-[#0F172A]">£0.00 <span className="text-xs font-normal text-gray-500">/ forever</span></span>
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-2">Free Core Platform</h3>
              <p className="text-sm text-[#475569] leading-relaxed mb-6">
                Small shops, family cafes, and trade contractors get our complete PIN tablet kiosk, rotas, holiday tracking, and the <strong>Dashboard Manager Copilot</strong> 100% free with zero credit card required.
              </p>
              <ul className="space-y-2.5 text-xs text-[#334155] font-medium border-t border-[#E8E2D8] pt-4">
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> Full PIN tablet kiosk &amp; attendance terminal</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> Dashboard Manager AI Copilot</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> 12.07% UK statutory holiday accrual</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> Pre-formatted Xero &amp; HMRC payroll exports</li>
              </ul>
            </div>

            <div className="bg-[#FDFBF7] border-2 border-emerald-300 rounded-3xl p-8 relative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
                  16+ Employees (PRO)
                </span>
                <span className="text-2xl font-black text-[#0F172A]">£3.49 <span className="text-xs font-normal text-gray-500">/ user / mo</span></span>
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-2">Unlocks WhatsApp Assistant Free</h3>
              <p className="text-sm text-[#475569] leading-relaxed mb-6">
                When your team grows to 16 seats, your £3.49/user subscription covers the cost of Meta messaging fees. We activate the <strong>24/7 Frontline WhatsApp Assistant</strong> for your entire team upon public rollout at zero extra charge.
              </p>
              <ul className="space-y-2.5 text-xs text-[#334155] font-medium border-t border-[#E8E2D8] pt-4">
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 font-bold" /> <strong>24/7 Frontline WhatsApp Assistant (Upon Rollout)</strong></li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> 1-tap WhatsApp manager approvals for leave</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> Live site GPS geofencing &amp; radar map</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> 0 Contracts — cancel or change capacity anytime</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMMON QUESTIONS / FAQS */}
      <section className="py-20 bg-[#FDFBF7] border-b border-[#E8E2D8]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-[#0F172A] tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-[#475569] text-base">
              Clear answers regarding our AI assistants, employee data security, and rollout plans.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white border border-[#E8E2D8] rounded-2xl overflow-hidden shadow-xs transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-5 text-left font-bold text-base text-[#0F172A] flex items-center justify-between gap-4 hover:bg-[#FDFBF7] transition-colors cursor-pointer"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform duration-200 shrink-0 ${
                      openFaq === i ? 'rotate-180 text-blue-600' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-sm text-[#475569] leading-relaxed border-t border-[#F1ECE4] pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CALL TO ACTION */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A] tracking-tight">
            Ready to simplify your staff scheduling?
          </h2>
          <p className="text-[#475569] text-base max-w-xl mx-auto leading-relaxed">
            Get started today with up to 15 employees for free. Set up your tablet PIN kiosk and test the Dashboard Manager Copilot in under two minutes.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-4 rounded-2xl text-base transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              Start Free (1-15 Staff) <ArrowRight size={18} />
            </button>
            <Link
              to="/pricing"
              className="w-full sm:w-auto bg-[#FDFBF7] border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-6 py-4 rounded-2xl text-base transition-all shadow-xs"
            >
              View All Pricing Tiers
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#F8F5EE] border-t border-[#E8E2D8] py-12 text-xs text-[#64748B]">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-bold text-sm text-[#0F172A]">
            <img src="/logo.png" alt="KithHR" className="w-6 h-6 object-contain" />
            <span>KithHR UK</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/tablet-time-clock-kiosk" className="hover:text-[#0F172A] transition-colors">Tablet Kiosk</Link>
            <Link to="/nfc-card-clock-in" className="hover:text-[#0F172A] transition-colors">NFC Clock-In</Link>
            <Link to="/pricing" className="hover:text-[#0F172A] transition-colors">Pricing</Link>
            <Link to="/privacy-policy" className="hover:text-[#0F172A] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#0F172A] transition-colors">Terms of Service</Link>
          </div>
          <div>© {new Date().getFullYear()} KithHR. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default AiWorkplaceAssistants;
